import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Página pública mobile-first para escanear cédula o seguro médico.
 * URL: /scan/:token — el token viene del QR generado por la secretaria.
 * NO requiere autenticación.
 */
export default function ScanCapture() {
  const { token } = useParams();
  const [phase, setPhase] = useState("checking"); // checking | ready | uploading | done | error
  const [errorMsg, setErrorMsg] = useState("");
  const [docKind, setDocKind] = useState("cedula");
  const fileRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/api/patient-scan/public/${token}`)
      .then(() => setPhase("ready"))
      .catch((e) => {
        setPhase("error");
        setErrorMsg(e.response?.data?.detail || "Enlace inválido");
      });
  }, [token]);

  const submit = async (file) => {
    if (!file) return;
    setPhase("uploading");
    try {
      const form = new FormData();
      form.append("image", file);
      form.append("doc_kind", docKind);
      await axios.post(`${API}/api/patient-scan/public/${token}/upload`, form);
      setPhase("done");
    } catch (e) {
      setErrorMsg(e.response?.data?.detail || "No se pudo subir la foto");
      setPhase("error");
    }
  };

  const bg = "#F5F5F5";
  return (
    <div
      style={{
        minHeight: "100vh",
        background: bg,
        padding: 24,
        fontFamily: "system-ui, -apple-system, sans-serif",
        color: "#163144",
      }}
    >
      <div
        style={{
          maxWidth: 480,
          margin: "0 auto",
          background: "#fff",
          borderRadius: 28,
          padding: 24,
          border: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: 3,
            color: "#01BC8E",
          }}
        >
          ConsuDoctor
        </div>
        <h1
          style={{
            fontSize: 24,
            fontWeight: 900,
            marginTop: 8,
            marginBottom: 8,
          }}
        >
          Registrar en la clínica
        </h1>

        {phase === "checking" && (
          <p style={{ color: "#1B405B99" }}>Verificando enlace…</p>
        )}

        {phase === "error" && (
          <div
            style={{
              background: "#fef2f2",
              color: "#b91c1c",
              padding: 16,
              borderRadius: 16,
              marginTop: 8,
              fontSize: 14,
            }}
          >
            <b>Ups:</b> {errorMsg}. Pídele a la secretaria un nuevo código QR.
          </div>
        )}

        {phase === "ready" && (
          <>
            <p style={{ color: "#1B405B99", fontSize: 14, lineHeight: 1.5 }}>
              Selecciona qué documento vas a fotografiar y toma la foto. Los
              datos se enviarán directamente a la secretaria de la clínica.
            </p>

            <div
              role="tablist"
              style={{ display: "flex", gap: 8, marginTop: 20 }}
            >
              {[
                ["cedula", "Cédula"],
                ["seguro", "Seguro médico"],
              ].map(([v, l]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setDocKind(v)}
                  style={{
                    flex: 1,
                    padding: "12px 8px",
                    borderRadius: 999,
                    border:
                      docKind === v
                        ? "2px solid #01BC8E"
                        : "1px solid rgba(0,0,0,0.1)",
                    background: docKind === v ? "#ECFDF5" : "#fff",
                    color: docKind === v ? "#065F46" : "#163144",
                    fontWeight: 800,
                    fontSize: 14,
                  }}
                >
                  {l}
                </button>
              ))}
            </div>

            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: "none" }}
              onChange={(e) => submit(e.target.files?.[0])}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              style={{
                width: "100%",
                marginTop: 24,
                padding: "18px",
                borderRadius: 20,
                background: "#01BC8E",
                color: "#fff",
                fontSize: 18,
                fontWeight: 900,
                border: "none",
                boxShadow: "0 8px 24px rgba(1,188,142,0.25)",
              }}
            >
              📷 Tomar foto del {docKind === "cedula" ? "cédula" : "seguro"}
            </button>

            <p
              style={{
                color: "#1B405B80",
                fontSize: 12,
                marginTop: 20,
                textAlign: "center",
                lineHeight: 1.5,
              }}
            >
              🔒 Tu foto NO se guarda en el sistema. Solo se extraen los datos
              y se descarta.
            </p>
          </>
        )}

        {phase === "uploading" && (
          <div style={{ textAlign: "center", padding: 40 }}>
            <div
              style={{
                width: 40,
                height: 40,
                margin: "0 auto",
                border: "3px solid #01BC8E",
                borderTopColor: "transparent",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
            <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
            <p style={{ marginTop: 20, color: "#1B405B" }}>
              Extrayendo los datos, dame unos segundos…
            </p>
          </div>
        )}

        {phase === "done" && (
          <div style={{ textAlign: "center", padding: 20 }}>
            <div
              style={{
                fontSize: 64,
                lineHeight: 1,
              }}
            >
              ✅
            </div>
            <h2
              style={{ fontSize: 22, fontWeight: 900, marginTop: 12 }}
            >
              ¡Listo!
            </h2>
            <p style={{ color: "#1B405B99", marginTop: 8 }}>
              Ya puedes cerrar esta pestaña. La secretaria verá tus datos en su
              pantalla.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
